<template>
  <div class="user-details-container">
    <div class="details-component__content content">
      <div class="content__cell">
        <table class="details-table">
          <tr class="details-table__row" v-if="canUseCards()">
            <td class="details-table__col details-table__col_label"
              :class="{ 'form__field_error': errors.has('parkingCardNr') }">{{ $t('common.card_number') }}</td>
            <td class="details-table__col">
              <el-input :placeholder="$t('common.card_number')" name="parkingCardNr" id="parkingCardNr"
                class="parkingCardNrInput-toggle" v-model="form.parkingCardNr" v-validate="'validParkingCardNr'"
                :data-vv-as="$t('add_permit.parking_card_id')" data-vv-delay="1000" ref="parkingCardNrInput"
                @keyup.enter.native="toggleInput('parkingCardNrInput');" :disabled="permitChangeDisabled">
                <el-button
                  :class="{ 'parkingCardNrInput-toggle': parkingCardNrInputDisabled, 'on-click-trigger-submit2': !parkingCardNrInputDisabled }"
                  slot="append" :icon="parkingCardNrInputDisabled || permitChangeDisabled ? 'el-icon-edit' : ''"
                  :disabled="permitChangeDisabled">{{ parkingCardNrInputDisabled || permitChangeDisabled ? '' :
                    $t('common.ok') }}</el-button>
              </el-input>
              <tooltip v-show="errors.has('parkingCardNr')" class="form__validation">{{ $t('errors.valid_card_field') }}
              </tooltip>
            </td>
          </tr>

          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $tc('common.user_vehicle_reg', 1) }}</td>
            <td class="details-table__col">
              <div class="el-input__inner el-input__inner--vehicle-reg">{{ selectedVehicle.vehicleReg }}</div>
              <el-button v-if="!permitChangeDisabled" class="btn btn--change" @click="showListModalVehicle()"
                slot="append">{{ $t('common.change') }} {{ $tc('common.plate', 1) }}</el-button>
            </td>
          </tr>

          <tr class="details-table__row" v-if="permit.parkingSpotNumber">
            <td class="details-table__col details-table__col_label">{{ $t('permits.parking_spot_number') }}</td>
            <td class="details-table__col">{{ permit.parkingSpotNumber }}</td>
          </tr>

          <tr class="details-table__row">
            <td class="details-table__col details-table__col_label">{{ $t('common.zone') }}</td>
            <td class="details-table__col" v-if="permit.product">
              <span v-for="zone in permit.product.zones" :key="zone.id" class="el-tag">{{ zone.code }}&nbsp;</span>
            </td>
          </tr>

          <tr class="details-table__row" v-if="permit.product">
            <td class="details-table__col details-table__col_label">{{ $t('common.product') }}</td>
            <td class="details-table__col">{{ permit.product.name }}</td>
          </tr>

          <tr class="details-table__row" v-if="permit.partner">
            <td class="details-table__col details-table__col_label">{{ $t('common.issued_by') }}</td>
            <td class="details-table__col">{{ permit.partner.name }}</td>
          </tr>

          <tr class="details-table__row" v-if="permit.partner">
            <td class="details-table__col details-table__col_label">{{ $t('end_user.contact') }}</td>
            <td class="details-table__col">
              {{[permit.partner.contactPerson,
              permit.partner.phone
                ? parsePhoneNumber(permit.partner.phone, permit.partner.phoneCountry)
                : null,
              permit.partner.address]
                .filter(x => x !== null)
                .join(',&nbsp;')}}
            </td>
          </tr>

          <tr class="details-table__row" v-if="permit.validFromDate">
            <td class="details-table__col details-table__col_label">{{ $t('permits.valid_from_date') }}</td>
            <td class="details-table__col">{{ permit.validFromDate }}</td>
          </tr>

          <tr class="details-table__row" v-if="permit.expirationDate">
            <td class="details-table__col details-table__col_label">{{ $t('permits.expiration_date') }}</td>
            <td class="details-table__col">{{ permit.expirationDate }}</td>
          </tr>

        </table>
      </div>
      <div id="user-map-dom"></div>
    </div>

    <VehicleSelectionModal
        :show="showListModal"
        :user-id="selectedUser"
        :active-vehicle-id="selectedVehicle.id"
        @close="closeListModal"
        @confirmed="confirmedListModal"
        v-if="selectedUser"
      />

    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">{{ userWarningHeader }}</h1>
      <p slot="body">{{ userWarningBody }}</p>
    </WarningModal>
    <ConfirmationModal v-if="showConfirmationModal" @cancel="closeConfirmationModal" @confirmed="uncheckVehicleActive">
      <h1 slot="header">{{ $t('common.vehicle_reg') }}</h1>
      <p slot="body">{{ $t('vehicle.confirm_uncheck_active') }}</p>
    </ConfirmationModal>
  </div>
</template>

<script>
import Vue from 'vue';
import { sha256 } from 'js-sha256';
import { mapGetters } from 'vuex';
import moment from 'moment';
import * as mTypes from 'store/mutation-types';
import L from 'leaflet';
import { format } from 'libphonenumber-js';
import Tooltip from 'common/Tooltip';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';
import VehicleSelectionModal from 'common/VehicleSelectionModal';

export default {
  components: {
    Tooltip,
    ConfirmationModal,
    WarningModal,
    VehicleSelectionModal,
  },
  computed: {
    ...mapGetters({
      permitError: 'permits/permit/error',
      permit: 'permits/permit/permit',
      coordinates: 'permits/permit/coordinates',
      user: 'user/user/user',
    }),
    selectedUser() {
      return this.user.data && this.user.data.selectedRole ? this.user.data.selectedRole.id : null;
    },
  },
  mounted() {
    this.initMap();

    this.mapInstance.on('load', () => {
      this.fetchAllData();
      this.resetValidators();
      this.createValidators();
      document.removeEventListener('click', window.eventListenerForEditViewPermit);
      window.eventListenerForEditViewPermit = this.handleInput;
      document.addEventListener('click', this.handleInput, false);
    });
  },
  watch: {
    $route: 'fetchAllData',
    permit: function (newPermit) {
      if (newPermit.vehicle && newPermit.vehicle.id !== this.selectedVehicle.id) {
        this.selectedVehicle = newPermit.vehicle;
      }
    },
  },
  data() {
    return {
      selectedVehicle: {
        id: null,
      },
      primordialSelectedVehicle: this.selectedVehicle,
      form: {},
      parkingCardNrInputDisabled: true,
      permitChangeDisabled: false,
      showConfirmationModal: false,
      showWarningModal: false,
      userWarningBody: null,
      userWarningHeader: null,
      zoom: 15,
      center: [24.707, 59.412],
      style: 'klokantech-basic',
      mapInstance: null,
      showListModal: false,
    };
  },
  methods: {
    initMap() {
      this.mapInstance = new mapboxgl.Map({
        container: 'user-map-dom',
        style: `${process.env.MAPS_URL}/styles/${this.style}/style.json`,
        hash: false,
        center: this.center,
        zoom: this.zoom,
      });
      this.mapInstance.addControl(new mapboxgl.NavigationControl());
    },
    cleanupCoordinates(coordinates) {
      let data = {};
      if (coordinates) {
        if (Array.isArray(coordinates) && coordinates[0] && coordinates[0].type === 'Feature') {
          data = {
            type: 'FeatureCollection',
            features: coordinates,
          };
        } else if (coordinates === Object(coordinates) && coordinates.type === 'Feature') {
          data = {
            type: 'FeatureCollection',
            features: [coordinates],
          };
        } else if (Array.isArray(coordinates) && coordinates[0] && coordinates[0].type === 'FeatureCollection') {
          const features = [];
          for (const c of coordinates) {
            for (const f of c.features) {
              features.push(f);
            }
          }
          data = {
            type: 'FeatureCollection',
            features,
          };
        } else if (coordinates === Object(coordinates) && coordinates.type === 'FeatureCollection') {
          data = coordinates;
        }
      }
      return data;
    },
    addSourceNLayers() {
      const id = sha256(JSON.stringify(this.coordinates));
      const data = this.cleanupCoordinates(this.coordinates, id);

      const source = this.mapInstance.getSource('zone-user-data');
      if (!source) {
        this.mapInstance.addSource('zone-user-data', {
          type: 'geojson',
          data,
        });
      } else {
        source.setData(data);
      }

      if (!this.mapInstance.getLayer('zone-outline-user')) {
        this.mapInstance.addLayer({
          id: 'zone-outline-user',
          type: 'fill',
          source: 'zone-user-data',
          layout: {},
          paint: {
            'fill-color': '#088',
            'fill-opacity': 0.8,
          },
        });
      }
    },
    updateMap() {
      if (this.mapInstance && this.coordinates && this.coordinates.length > 0) {
        const bounds = L.geoJSON(this.coordinates).getBounds();
        if (bounds.isValid()) {
          const sw = bounds.getSouthWest();
          const ne = bounds.getNorthEast();
          const llb = new mapboxgl.LngLatBounds(sw, ne);
          this.mapInstance.fitBounds(llb, { padding: 20 });
        }
        Vue.nextTick(() => {
          if (this.coordinates && this.coordinates.length > 0) {
            if (this.mapInstance.isStyleLoaded()) {
              this.addSourceNLayers();
            } else {
              this.mapInstance.on('load', () => {
                this.addSourceNLayers();
              });
            }
          }
        });
      }
    },
    fetchAllData() {
      this.fetchPermit();
    },
    closeConfirmationModal() {
      this.showConfirmationModal = false;
    },
    uncheckVehicleActive() {
      this.selectedVehicle = { id: null, vehicleReg: null };
      this.closeConfirmationModal();
      this.onSubmit();
    },
    toggleInput(buttonRef) {
      if (!this[`${buttonRef}Disabled`]) {
        this.onSubmit();
      }
      this[`${buttonRef}Disabled`] = !this[`${buttonRef}Disabled`];
      if (buttonRef !== 'vehicleInput') {
        this.blurInput(buttonRef);
      }
    },
    handleInput(event) {
      if (event.target && (event.target.classList.contains('on-click-trigger-submit2') || event.target.parentElement.classList.contains('on-click-trigger-submit2'))) {
        this.onSubmit();
      }
      let buttonRef = false;
      this.parkingCardNrInputDisabled = true;
      if (event.target && (event.target.classList.contains('parkingCardNrInput-toggle') || event.target.parentElement.classList.contains('parkingCardNrInput-toggle'))) {
        buttonRef = 'parkingCardNrInput';
      }
      if (buttonRef) {
        this[`${buttonRef}Disabled`] = false;
        this.focusInput(buttonRef);
      }
    },
    focusInput(buttonRef) {
      if (!this[`${buttonRef}Disabled`]) {
        this.$nextTick(() => this.$refs[buttonRef].$refs.input.focus());
      }
    },
    blurInput(buttonRef) {
      this.$nextTick(() => this.$refs[buttonRef].$refs.input.blur());
    },
    checkedDateForPermitChangeDisabled() {
      if (moment().isAfter(moment(this.permit.expirationDate, 'DD.MM.YYYY'), 'day')) {
        this.permitChangeDisabled = true;
      } else {
        this.permitChangeDisabled = false;
      }
    },
    fetchPermit() {
      if (this.$route.params.id && this.user.data) {
        this.$store.dispatch('permits/permit/fetch', { id: this.$route.params.id, user: this.user })
          .then(() => {
            this.form = this.permit;
            this.setPermitRelatedVehicle();
            this.updateMap();
            this.checkedDateForPermitChangeDisabled();
          });
      } else {
        this.$store.commit(`permits/permit/${mTypes.CLEAR_STATE}`);
      }
    },
    setPermitRelatedVehicle() {
      if (this.permit.vehicle) {
        this.selectedVehicle = this.permit.vehicle;
      } else {
        this.selectedVehicle = { id: null };
      }
    },
    parsePhoneNumber(phone, code) {
      return phone
        ? format({ country: code, phone: phone.toString() }, 'International_plaintext')
        : '';
    },
    onSubmit() {
      if (!this.permitChangeDisabled) {
        this.$validator.validateAll()
          .then((success) => {
            if (success) {
              this.$store.commit(`permits/permit/${mTypes.DATA_ERROR}`, null);

              if (this.selectedVehicle.new) {
                this.form.vehicle = { vehicleReg: this.selectedVehicle.vehicle_reg, comment: this.selectedVehicle.comment };
              } else if (this.selectedVehicle.id !== null) {
                this.form.vehicle = this.selectedVehicle;
              } else {
                delete this.form.vehicle;
              }

              if (!this.form.parkingCardNr || !this.canUseCards()) {
                this.form.parkingCardNr = null;
              }
              this.form.userId = this.selectedUser;
              this.form.id = this.permit.id;

              if (this.form.vehicle && !this.form.vehicle.id) {
                  this.$store.dispatch('permits/permit/saveNewVehicle', {
                    vehicle_reg: this.form.vehicle.vehicleReg.toUpperCase(),
                    comment: this.form.vehicle.comment,
                    userId: this.selectedUser,
                  }).then((response) => {
                    this.form.vehicleId = response.body.data.id;
                    this.savePermit();
                  }).catch((error) => {
                    const { message } = error.body;
                    this.showWarningModal = true;
                    this.userWarningHeader = this.$t('common.warning');
                    this.userWarningBody = this.$t(`errors.${message}`);
                  });
              } else {
                  if(this.form.vehicle) {
                      this.form.vehicleId = this.form.vehicle.id;
                  }
                  this.savePermit();
              }
            }
          });
      }
    },
    savePermit() {
      this.$store.dispatch('permits/permit/savePermit', { user: this.user, permit: this.form })
        .then(() => {
          if (!this.permitError) {
            this.fetchAllData();
            this.parkingCardNrInputDisabled = true;
          }
        });
    },
    closeWarningModal() {
      this.showWarningModal = false;
    },
    resetValidators() {
      this.$validator.remove('validParkingCardNr');
      this.$validator.clean();
    },
    createValidators() {
      this.createParkingCardValidator();
    },
    createParkingCardValidator() {
      this.$validator.extend('validParkingCardNr', {
        getMessage: field => `The ${field} value is not valid parking card number.`,
        validate: (value) => {
          if (value === '') {
            return true;
          }
          return /^[0-9A-F]{9}$/i.test(value);
        },
      });
    },
    canUseCards() {
      if (this.permit.product) {
        return this.permit.product.canUseCards;
      }
      return false;
    },
    showListModalVehicle() {
      this.primordialSelectedVehicle = this.selectedVehicle;
      this.showListModal = true;
    },
    closeListModal() {
      this.selectedVehicle = this.primordialSelectedVehicle;
      this.showListModal = false;
      this.onSubmit();
    },
    confirmedListModal(vehicle) {
      this.selectedVehicle = vehicle;
      this.primordialSelectedVehicle = vehicle;
      this.showListModal = false;
      this.onSubmit();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/app.scss";
@import "~styles/blocks/permit-edit.scss";
@import "~node_modules/leaflet/dist/leaflet.css";

.user-details-container {
  background-color: var(--white);
  display: inline-block;
  margin: 0 0 0 20px;
  padding: 10px 35px;
  flex: 1;

  @media screen and (max-width: 1074px) {
    padding: 10px;
  }

  @media screen and (max-width: 760px) {
    display: none;
  }

  >.content {
    @media screen and (max-width: 1074px) {
      flex-direction: column;
    }
  }

  .product-map {
    @media screen and (max-width: 1074px) {
      flex-direction: column;
      margin-left: 0;
    }
  }

  .details-table__row:nth-child(2) {
    .details-table__col {
      padding-top: 7px;
      vertical-align: top;
    }
  }

  .details-table__row {
    .details-table__col {
      position: relative;
      padding-top: 7px;

      .el-input {
        border-spacing: 0;
        max-width: calc(100% - 4px);
        position: relative;

        &__inner {
          &--vehicle-reg {
            position: relative;
            width: fit-content;
            height: auto;
            line-height: 1.15;
            padding: 10px 15px;
            margin: 2px;
            margin-left: 30px;
            border-radius: 0 4px 4px 0;

            &::after {
              content: '';
              position: absolute;
              top: -1px;
              left: -30px;
              width: 30px;
              height: 40px;
              border: 1px solid #dcdfe6;
              border-radius: 4px 0 0 4px;
              box-sizing: border-box;
            }
          }
        }
      }

      .el-button:not(.btn--change):not(.vehicle-input) {
        width: 54px;
      }
    }
  }
}
</style>

<style lang="scss">
@import "~styles/app.scss";

.el-button {
  vertical-align: middle;
}

.user-details-container {
  .details-table {
    &__row {
      .el-button {
        height: 40px;
        vertical-align: middle;

        span {
          margin: 0;
          white-space: normal;
        }
      }

      .btn--change {
        display: inline-flex;
        text-transform: uppercase;
        background-color: var(--brand-green);
        color: var(--white);
        padding: 10px 15px;
        vertical-align: top;

        span {
          width: auto !important;
        }
      }

      .el-input {
        &__suffix {
          z-index: 2;
          right: 0;
        }
      }

      .tooltip {
        top: 50%;
        right: auto;
        left: 50px;
        z-index: 2000;
        transform: translateY(-50%);
        max-height: none;

        &__content {
          @media screen and (max-width: 1000px) {
            max-height: none;
            height: auto;
            white-space: pre-wrap;
            max-width: 150px;
          }
        }
      }
    }
  }
}

#user-map-dom {
  margin-top: 30px;
  width: 100%;
  height: 300px;
}
</style>



// WEBPACK FOOTER //
// src/components/end-user/EditViewPermit.vue